/**
 * Product Grid Component
 * 
 * High-performance product grid with virtualization for 10,000+ products,
 * filtering, sorting, and infinite scroll capabilities.
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import { Product, ProductParams } from '../../types/ecommerce';
import { ProductCard } from './ProductCard';
import { LoadingSpinner } from '../LoadingSpinner';
import { Button } from '../Button';

/**
 * Product grid props interface
 */
interface ProductGridProps {
  /** Products to display */
  products: Product[];
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: string | null;
  /** Grid layout configuration */
  layout?: 'grid' | 'list';
  /** Number of columns for grid layout */
  columns?: number;
  /** Enable virtualization for large datasets */
  virtualized?: boolean;
  /** Enable infinite scroll */
  infiniteScroll?: boolean;
  /** Product filtering parameters */
  filters?: ProductParams;
  /** Sorting configuration */
  sort?: {
    field: keyof Product;
    direction: 'asc' | 'desc';
  };
  /** Callback for loading more products */
  onLoadMore?: () => Promise<void>;
  /** Callback for product click */
  onProductClick?: (product: Product) => void;
  /** Callback for filter change */
  onFilterChange?: (filters: ProductParams) => void;
  /** Callback for sort change */
  onSortChange?: (sort: { field: keyof Product; direction: 'asc' | 'desc' }) => void;
  /** Custom CSS classes */
  className?: string;
  /** Whether to show filtering controls */
  showFilters?: boolean;
  /** Whether to show sorting controls */
  showSort?: boolean;
  /** Grid item size for virtualization */
  itemSize?: { width: number; height: number };
}

/**
 * Filter controls component
 */
interface FilterControlsProps {
  filters: ProductParams;
  onFilterChange: (filters: ProductParams) => void;
  productCount: number;
}

const FilterControls: React.FC<FilterControlsProps> = ({
  filters,
  onFilterChange,
  productCount,
}) => {
  const [localFilters, setLocalFilters] = useState<ProductParams>(filters);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFilterChange = useCallback((key: keyof ProductParams, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  }, [localFilters, onFilterChange]);

  const clearFilters = useCallback(() => {
    const clearedFilters: ProductParams = {};
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  }, [onFilterChange]);

  const hasActiveFilters = useMemo(() => Object.values(localFilters).some(value => 
      value !== undefined && value !== '' && value !== null
    ), [localFilters]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Filters
          </h3>
          <span className="text-sm text-gray-500">
            {productCount} products
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <Button
              onClick={clearFilters}
              variant="outline"
              size="sm"
            >
              Clear All
            </Button>
          )}
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="ghost"
            size="sm"
          >
            {isExpanded ? 'Less' : 'More'} Filters
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            type="text"
            value={localFilters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search products..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={localFilters.category || ''}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            <option value="electronics">Electronics</option>
            <option value="clothing">Clothing</option>
            <option value="books">Books</option>
            <option value="home">Home & Garden</option>
          </select>
        </div>

        {/* Price Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Min Price
          </label>
          <input
            type="number"
            value={localFilters.minPrice || ''}
            onChange={(e) => handleFilterChange('minPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder="$0"
            min="0"
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Price
          </label>
          <input
            type="number"
            value={localFilters.maxPrice || ''}
            onChange={(e) => handleFilterChange('maxPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder="$999"
            min="0"
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Stock Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stock Status
          </label>
          <select
            value={localFilters.stockStatus || ''}
            onChange={(e) => handleFilterChange('stockStatus', e.target.value as any)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Items</option>
            <option value="instock">In Stock</option>
            <option value="outofstock">Out of Stock</option>
            <option value="onbackorder">On Backorder</option>
          </select>
        </div>

        {/* Featured/Sale toggles */}
        <div className="flex flex-col gap-2">
          <label className="block text-sm font-medium text-gray-700">
            Special Items
          </label>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="featured"
              checked={localFilters.featured || false}
              onChange={(e) => handleFilterChange('featured', e.target.checked || undefined)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="featured" className="text-sm text-gray-700">
              Featured
            </label>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sale"
              checked={localFilters.onSale || false}
              onChange={(e) => handleFilterChange('onSale', e.target.checked || undefined)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="sale" className="text-sm text-gray-700">
              On Sale
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Sort controls component
 */
interface SortControlsProps {
  sort: { field: keyof Product; direction: 'asc' | 'desc' };
  onSortChange: (sort: { field: keyof Product; direction: 'asc' | 'desc' }) => void;
}

const SortControls: React.FC<SortControlsProps> = ({ sort, onSortChange }) => {
  const handleSortChange = useCallback((field: keyof Product) => {
    const direction = sort.field === field && sort.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ field, direction });
  }, [sort, onSortChange]);

  const sortOptions = [
    { field: 'name' as keyof Product, label: 'Name' },
    { field: 'price' as keyof Product, label: 'Price' },
    { field: 'dateCreated' as keyof Product, label: 'Date Added' },
    { field: 'featured' as keyof Product, label: 'Featured' },
  ];

  return (
    <div className="flex items-center gap-4 mb-6">
      <span className="text-sm font-medium text-gray-700">Sort by:</span>
      <div className="flex flex-wrap gap-2">
        {sortOptions.map((option) => (
          <Button
            key={option.field}
            onClick={() => handleSortChange(option.field)}
            variant={sort.field === option.field ? 'primary' : 'outline'}
            size="sm"
            className="flex items-center gap-1"
          >
            {option.label}
            {sort.field === option.field && (
              <span className="text-xs">
                {sort.direction === 'asc' ? '↑' : '↓'}
              </span>
            )}
          </Button>
        ))}
      </div>
    </div>
  );
};

/**
 * Virtualized grid item component
 */
interface GridItemProps {
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
  data: {
    products: Product[];
    columnsPerRow: number;
    itemWidth: number;
    onProductClick?: (product: Product) => void;
  };
}

const GridItem: React.FC<GridItemProps> = ({ columnIndex, rowIndex, style, data }) => {
  const { products, columnsPerRow, onProductClick } = data;
  const productIndex = rowIndex * columnsPerRow + columnIndex;
  const product = products[productIndex];

  if (!product) {
    return <div style={style} />;
  }

  return (
    <div style={{ ...style, padding: '8px' }}>
      <ProductCard
        product={product}
        variant="grid"
        onClick={onProductClick}
        className="h-full"
        priority={productIndex < 6} // Load first 6 products with priority
      />
    </div>
  );
};

/**
 * Product Grid Component
 * 
 * Optimized for performance with React Window virtualization
 * and comprehensive filtering/sorting capabilities.
 */
export const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  loading = false,
  error = null,
  layout = 'grid',
  columns = 4,
  virtualized = false,
  infiniteScroll = false,
  filters = {},
  sort = { field: 'name', direction: 'asc' },
  onLoadMore,
  onProductClick,
  onFilterChange,
  onSortChange,
  className = '',
  showFilters = true,
  showSort = true,
  itemSize = { width: 300, height: 400 },
}) => {
  const gridRef = useRef<any>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Responsive columns based on screen size
  const responsiveColumns = useMemo(() => {
    if (typeof window === 'undefined') return columns;
    
    const width = window.innerWidth;
    if (width < 640) return 1; // sm
    if (width < 768) return 2; // md
    if (width < 1024) return 3; // lg
    return columns;
  }, [columns]);

  // Sorted and filtered products
  const processedProducts = useMemo(() => {
    let filtered = [...products];

    // Apply filters
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm) ||
        product.description.toLowerCase().includes(searchTerm)
      );
    }

    if (filters.category) {
      filtered = filtered.filter(product =>
        product.categories.some(cat => cat.slug === filters.category)
      );
    }

    if (filters.minPrice !== undefined) {
      filtered = filtered.filter(product => product.price >= filters.minPrice!);
    }

    if (filters.maxPrice !== undefined) {
      filtered = filtered.filter(product => product.price <= filters.maxPrice!);
    }

    if (filters.stockStatus) {
      filtered = filtered.filter(product => product.inventory.stockStatus === filters.stockStatus);
    }

    if (filters.featured) {
      filtered = filtered.filter(product => product.featured);
    }

    if (filters.onSale) {
      filtered = filtered.filter(product => product.salePrice && product.salePrice > 0);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sort.field];
      const bValue = b[sort.field];
      
      let comparison = 0;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
      }
      
      return sort.direction === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [products, filters, sort]);

  // Handle infinite scroll
  const handleLoadMore = useCallback(async () => {
    if (!onLoadMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      await onLoadMore();
    } catch (error) {
      console.error('Failed to load more products:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [onLoadMore, isLoadingMore]);

  // Virtualization grid data
  const gridData = useMemo(() => ({
    products: processedProducts,
    columnsPerRow: responsiveColumns,
    itemWidth: itemSize.width,
    onProductClick,
  }), [processedProducts, responsiveColumns, itemSize.width, onProductClick]);

  // Calculate grid dimensions
  const rowCount = Math.ceil(processedProducts.length / responsiveColumns);
  const containerWidth = responsiveColumns * itemSize.width;
  const containerHeight = Math.min(600, rowCount * itemSize.height); // Max height of 600px

  // Error state
  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
        <div className="text-red-500 text-xl mb-4">⚠️ Error Loading Products</div>
        <p className="text-gray-600 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()} variant="primary">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Filter Controls */}
      {showFilters && onFilterChange && (
        <FilterControls
          filters={filters}
          onFilterChange={onFilterChange}
          productCount={processedProducts.length}
        />
      )}

      {/* Sort Controls */}
      {showSort && onSortChange && (
        <SortControls
          sort={sort}
          onSortChange={onSortChange}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
          <span className="ml-3 text-gray-600">Loading products...</span>
        </div>
      )}

      {/* Empty State */}
      {!loading && processedProducts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📦</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your filters or search terms.</p>
          {onFilterChange && (
            <Button onClick={() => onFilterChange({})} variant="primary">
              Clear Filters
            </Button>
          )}
        </div>
      )}

      {/* Product Grid */}
      {!loading && processedProducts.length > 0 && (
        <>
          {virtualized && processedProducts.length > 100 ? (
            // Virtualized grid for large datasets
            <div className="flex justify-center">
              <Grid
                ref={gridRef}
                columnCount={responsiveColumns}
                columnWidth={itemSize.width}
                height={containerHeight}
                rowCount={rowCount}
                rowHeight={itemSize.height}
                width={containerWidth}
                itemData={gridData}
              >
                {GridItem}
              </Grid>
            </div>
          ) : (
            // Standard grid for smaller datasets
            <div 
              data-testid="product-grid"
              className={`grid gap-6 ${
              layout === 'grid' 
                ? `grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-${responsiveColumns}`
                : 'grid-cols-1'
            }`}>
              {processedProducts.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  variant={layout === 'grid' ? 'grid' : 'list'}
                  onClick={onProductClick}
                  priority={index < 6} // Load first 6 products with priority
                />
              ))}
            </div>
          )}

          {/* Load More Button for Infinite Scroll */}
          {infiniteScroll && onLoadMore && (
            <div className="flex justify-center mt-8">
              <Button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                variant="outline"
                size="lg"
              >
                {isLoadingMore ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" />
                    Loading more...
                  </div>
                ) : (
                  'Load More Products'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductGrid;